import React from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import { FieldPhoneNumberInput, FieldTextInput, Form, PrimaryButton } from '../../../components';
import { FormattedMessage, useIntl } from 'react-intl';
import * as validators from '../../../util/validators';

import css from './SectionContactUs.module.css';

const MESSAGE_MAX_CHARACTERS = 1600;

// Hidden input field
const FieldHidden = props => {
  const { name } = props;
  return (
    <Field id={name} name={name} type="hidden" className={css.unitTypeHidden}>
      {fieldRenderProps => <input {...fieldRenderProps?.input} />}
    </Field>
  );
};

const ContactUsForm = props => {
  const { ...restOfProps } = props;

  const intl = useIntl();

  //name
  const nameRequired = validators.required(
    intl.formatMessage({
      id: 'ContactUsPage.nameRequired',
    })
  );

  // email
  const emailRequired = validators.required(
    intl.formatMessage({
      id: 'SignupForm.emailRequired',
    })
  );
  const emailValid = validators.emailFormatValid(
    intl.formatMessage({
      id: 'SignupForm.emailInvalid',
    })
  );

  //subject
  const subjectRequired = validators.required(
    intl.formatMessage({ id: 'ContactUsPage.subjectRequired' })
  );

  //message
  const messageRequired = validators.required(
    intl.formatMessage({ id: 'ContactUsPage.messageRequired' })
  );

  const messageMaxLength = validators.maxLength(
    intl.formatMessage(
      { id: 'ContactUsPage.messageMaxLength' },
      { maxLength: MESSAGE_MAX_CHARACTERS }
    ),
    MESSAGE_MAX_CHARACTERS
  );

  return (
    <FinalForm
      {...restOfProps}
      render={formRenderProps => {
        const {
          handleSubmit,
          initialValues,
          invalid,
          pristine,
          submitting,
          form,
        } = formRenderProps;
        const { name, email } = initialValues || {};
        const submitDisabled = invalid || pristine || submitting;

        return (
          <Form
            className={css.form}
            onSubmit={values => {
              handleSubmit(values)?.then(() => {
                form.restart();
              });
            }}
          >
            <FieldHidden id="userId" name="userId" />
            {name ? (
              <FieldHidden id="name" name="name" />
            ) : (
              <FieldTextInput
                type="text"
                id="name"
                name="name"
                autoComplete="off"
                label={<FormattedMessage id="ContactUsPage.name" />}
                placeholder={intl.formatMessage({ id: 'ContactUsPage.namePlaceholder' })}
                validate={nameRequired}
              />
            )}

            {email ? (
              <FieldHidden id="email" name="email" />
            ) : (
              <FieldTextInput
                type="email"
                id="email"
                name="email"
                autoComplete="email"
                label={intl.formatMessage({
                  id: 'SignupForm.emailLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.emailPlaceholder',
                })}
                validate={validators.composeValidators(emailRequired, emailValid)}
              />
            )}
            <FieldPhoneNumberInput
              className={css.phoneNumberRoot}
              type="text"
              id="phone"
              name="phone"
              autoComplete="phone-number"
              label={intl.formatMessage({
                id: 'ContactUsPage.phoneNumber',
              })}
              placeholder={intl.formatMessage({
                id: 'SignupForm.phoneNumberPlaceholder',
              })}
            />
            <FieldTextInput
              type="text"
              id="subject"
              name="subject"
              autoComplete="off"
              label={<FormattedMessage id="ContactUsPage.subject" />}
              placeholder={intl.formatMessage({ id: 'ContactUsPage.subjectPlaceholder' })}
              validate={subjectRequired}
            />

            <FieldTextInput
              type="textarea"
              id="message"
              name="message"
              autoComplete="off"
              label={<FormattedMessage id="ContactUsPage.message" />}
              placeholder={intl.formatMessage({ id: 'ContactUsPage.messagePlaceholder' })}
              validate={validators.composeValidators(messageRequired, messageMaxLength)}
              format={value => (value ? value?.trim() : '')}
              formatOnBlur
            />

            <div className={css.submit}>
              <PrimaryButton type="submit" disabled={submitDisabled} inProgress={submitting}>
                {intl.formatMessage({ id: 'ContactUsPage.submitBtn' })}
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

export default ContactUsForm;
